import {Info} from 'lucide-react'
import { useState } from "react"

interface CalcInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  prefix?: string
  suffix?: string
  placeholder?: string
  tooltip?: string
}

function CalcInput({ label, value, onChange, prefix, suffix, placeholder, tooltip }: CalcInputProps) {
  const [showTip, setShowTip] = useState(false)

  const handleChange = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "")
    const parts = cleaned.split(".")
    const normalized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned
    onChange(normalized)
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</label>
        {tooltip ? (
          <span
            className="relative inline-flex"
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
          >
            <button
              type="button"
              onClick={() => setShowTip((prev) => !prev)}
              aria-label={`More info about ${label}`}
              className="text-slate-400 hover:text-emerald-600 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
            {showTip ? (
              <span
                role="tooltip"
                className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 sm:w-64 rounded-lg bg-slate-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg"
              >
                {tooltip}
                <span className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-slate-900 rotate-45" />
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="relative">
        {prefix ? (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">{prefix}</span>
        ) : null}
        <input
          type="text"
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          className={`w-full rounded-lg border border-slate-200 bg-slate-50/80 text-slate-900 font-semibold text-[15px] py-3 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 focus:bg-white transition-all ${
            prefix ? "pl-8" : "pl-3.5"
          } ${suffix ? "pr-9" : "pr-3.5"}`}
        />
        {suffix ? (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">{suffix}</span>
        ) : null}
      </div>
    </div>
  )
}

export default CalcInput
